import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    IonModal,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonButton,
    IonContent,
    IonItem,
    IonLabel,
    IonCheckbox,
    IonSearchbar,
    IonList,
    IonNote,
    IonFooter,
    IonTextarea,
    IonSpinner,
    IonIcon,
    useIonToast,
} from "@ionic/react";
import { peopleOutline, checkmarkCircleOutline, closeCircleOutline } from "ionicons/icons";

/* ====== Replace these with your real functions ====== */
import {
    actionToGetLatestUsersByFilter,
    actionToStoreNotification
} from "../apiHelpers/CommonAction";

/* ==================================================== */

export default function AdminSendNotificationModal({
                                                       isOpen,
                                                       onDismiss,
                                                       defaultMessage = "",
                                                   }) {
    const [presentToast] = useIonToast();

    // UI state
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [users, setUsers] = useState([]); // [{id,name,email}]
    const [query, setQuery] = useState("");
    const [sendToAll, setSendToAll] = useState(false);
    const [selected, setSelected] = useState(() => new Set());
    const [message, setMessage] = useState(defaultMessage);
    const [objectName, setObjectName] = useState("");
    const [objectId, setObjectId] = useState("");
    const [sending, setSending] = useState(false);
    const [progress, setProgress] = useState({ done: 0, total: 0, failed: 0 });

    const abortRef = useRef(null);

    // Load users when modal opens
    useEffect(() => {
        if (!isOpen) return;
        setQuery("");
        setSendToAll(false);
        setSelected(new Set());
        setProgress({ done: 0, total: 0, failed: 0 });
        setMessage(defaultMessage || "");

        const controller = new AbortController();
        abortRef.current = controller;

        (async () => {
            try {
                setLoadingUsers(true);
                const list = await actionToGetLatestUsersByFilter();
                // Basic dedupe by id
                const seen = new Set();
                const cleaned = list.filter((u) => {
                    if (!u?.id || seen.has(u.id)) return false;
                    seen.add(u.id);
                    return true;
                });
                setUsers(cleaned);
            } catch (err) {
                if (controller.signal.aborted) return;
                presentToast({
                    message: err?.message || "Failed to load users",
                    duration: 2000,
                    color: "danger",
                });
            } finally {
                setLoadingUsers(false);
            }
        })();

        return () => {
            controller.abort();
        };
    }, [isOpen, defaultMessage, presentToast]);

    // Filtered users by query
    const filtered = useMemo(() => {
        const q = (query || "").trim().toLowerCase();
        if (!q) return users;
        return users.filter((u) =>
            [u?.name, u?.email, String(u?.id)].some((v) =>
                String(v || "").toLowerCase().includes(q)
            )
        );
    }, [users, query]);

    const allFilteredSelected = useMemo(() => {
        if (!filtered.length) return false;
        return filtered.every((u) => selected.has(u.id));
    }, [filtered, selected]);

    const toggleOne = (userId) => {
        setSelected((prev) => {
            const n = new Set(prev);
            if (n.has(userId)) n.delete(userId);
            else n.add(userId);
            return n;
        });
    };

    const toggleAllFiltered = () => {
        setSelected((prev) => {
            const n = new Set(prev);
            if (allFilteredSelected) {
                // unselect all filtered
                filtered.forEach((u) => n.delete(u.id));
            } else {
                // select all filtered
                filtered.forEach((u) => n.add(u.id));
            }
            return n;
        });
    };

    // Submission helpers
    const canSend = useMemo(() => {
        if (!message?.trim()) return false;
        if (sendToAll) return !!users.length;
        return selected.size > 0;
    }, [message, sendToAll, users.length, selected.size]);

    // Small concurrency pool to avoid hammering backend
    async function runBatched(items, worker, concurrency = 10, onStep) {
        let idx = 0;
        let active = 0;
        let failed = 0;
        return new Promise((resolve) => {
            function next() {
                while (active < concurrency && idx < items.length) {
                    const current = items[idx++];
                    active++;
                    worker(current)
                        .catch(() => {
                            failed++;
                        })
                        .finally(() => {
                            active--;
                            onStep?.(idx, failed, items.length);
                            if (idx >= items.length && active === 0) {
                                resolve({ total: items.length, failed });
                            } else {
                                next();
                            }
                        });
                }
            }
            next();
        });
    }

    const handleSend = async () => {
        if (!canSend || sending) return;
        setSending(true);
        setProgress({ done: 0, total: 0, failed: 0 });

        try {
            // Determine targets
            const targetUserIds = sendToAll ? users.map((u) => u.id) : Array.from(selected);

            // Prepare payload factory
            const base = {
                message: message.trim(),
                object_id: objectId?.trim() || null,
                object_name: objectName?.trim() || null,
                is_created_for_admin: 0, // sending to users
            };

            setProgress({ done: 0, total: targetUserIds.length, failed: 0 });

            // Use batched sending with small concurrency
            const result = await runBatched(
                targetUserIds,
                async (uid) =>
                    actionToStoreNotification({
                        ...base,
                        user_id: uid,
                    }),
                10,
                (done, failed, total) => {
                    setProgress({ done, failed, total });
                }
            );

            if (result.failed === 0) {
                presentToast({
                    message: `Sent to ${result.total} user${result.total === 1 ? "" : "s"} successfully.`,
                    duration: 2200,
                    color: "success",
                });
                // Optional: clear selection/message after success
                setSelected(new Set());
                // Keep message intact in case admin wants to send again; comment next line if preferred
                // setMessage("");
                onDismiss?.();
            } else {
                presentToast({
                    message: `Delivered ${result.total - result.failed}/${result.total}. Some sends failed.`,
                    duration: 2600,
                    color: "warning",
                });
            }
        } catch (err) {
            presentToast({
                message: err?.message || "Failed to send notifications",
                duration: 2200,
                color: "danger",
            });
        } finally {
            setSending(false);
        }
    };

    return (
        <IonModal isOpen={isOpen} onDidDismiss={onDismiss} initialBreakpoint={1} breakpoints={[0, 1]}>
            <IonHeader>
                <IonToolbar>
                    <IonTitle>Send Notification</IonTitle>
                    <IonButtons slot="end">
                        <IonButton onClick={onDismiss}>Close</IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent className="ion-padding">

                {/* Message */}
                <IonItem lines="full">
                    <IonLabel position="stacked">Message</IonLabel>
                    <IonTextarea
                        value={message}
                        autoGrow
                        rows={4}
                        placeholder="Type the notification message…"
                        onIonInput={(e) => setMessage(e.detail.value || "")}
                    />
                </IonItem>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, opacity: 0.75, margin: "6px 8px 12px" }}>
                    <IonNote>{message.trim().length} chars</IonNote>
                    {sending && (
                        <IonNote>
                            {progress.done}/{progress.total} sent
                            {progress.failed ? ` • ${progress.failed} failed` : ""}
                        </IonNote>
                    )}
                </div>

                {/* Optional meta (maps to object_id / object_name in your API) */}
                <div style={{ display: "grid", gap: 8 }}>
                    <IonItem lines="full">
                        <IonLabel position="stacked">Object name (optional)</IonLabel>
                        <input
                            value={objectName}
                            onChange={(e) => setObjectName(e.target.value)}
                            placeholder="e.g., job-portal, assignments, newsletter, events"
                            style={{ width: "100%", padding: 10, border: "1px solid var(--ion-color-medium)", borderRadius: 8 }}
                        />
                    </IonItem>
                    <IonItem lines="full">
                        <IonLabel position="stacked">Object ID (optional)</IonLabel>
                        <input
                            value={objectId}
                            onChange={(e) => setObjectId(e.target.value)}
                            placeholder="e.g., 42"
                            style={{ width: "100%", padding: 10, border: "1px solid var(--ion-color-medium)", borderRadius: 8 }}
                        />
                    </IonItem>
                </div>

                {/* Audience selector */}
                <IonItem lines="full" style={{ marginTop: 12 }}>
                    <IonCheckbox
                        checked={sendToAll}
                        onIonChange={(e) => setSendToAll(!!e.detail.checked)}
                        slot="start"
                    />
                    <IonLabel>Send to all users</IonLabel>
                    <IonIcon icon={peopleOutline} slot="end" />
                </IonItem>

                {!sendToAll && (
                    <>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }}>
                            <IonSearchbar
                                debounce={250}
                                value={query}
                                onIonInput={(e) => setQuery(e.detail.value || "")}
                                placeholder="Search users by name, email, or ID…"
                                style={{ flex: 1 }}
                            />
                            <IonButton onClick={toggleAllFiltered} fill="outline">
                                {allFilteredSelected ? "Unselect filtered" : "Select filtered"}
                            </IonButton>
                        </div>

                        <div style={{ display: "flex", justifyContent: "space-between", padding: "0 12px", marginBottom: 8 }}>
                            <IonNote>
                                Showing {filtered.length}/{users.length}
                            </IonNote>
                            <IonNote>
                                Selected: {selected.size}
                            </IonNote>
                        </div>

                        <div style={{ border: "1px solid var(--ion-color-medium)", borderRadius: 10, overflow: "hidden" }}>
                            {loadingUsers ? (
                                <div style={{ padding: 16, display: "flex", gap: 8, alignItems: "center" }}>
                                    <IonSpinner name="dots" /> Loading users…
                                </div>
                            ) : filtered.length === 0 ? (
                                <div style={{ padding: 16, opacity: 0.65 }}>No users match your search.</div>
                            ) : (
                                <IonList style={{ maxHeight: 300, overflowY: "auto", margin: 0 }}>
                                    {filtered.map((u) => (
                                        <IonItem key={u.id} button onClick={() => toggleOne(u.id)}>
                                            <IonLabel>
                                                <div style={{ fontWeight: 600 }}>{u.name || "(no name)"}</div>
                                                <div style={{ fontSize: 12, opacity: 0.8 }}>{u.email}</div>
                                            </IonLabel>
                                            {selected.has(u.id) ? (
                                                <IonIcon icon={checkmarkCircleOutline} slot="end" color="success" />
                                            ) : (
                                                <IonIcon icon={closeCircleOutline} slot="end" color="medium" />
                                            )}
                                        </IonItem>
                                    ))}
                                </IonList>
                            )}
                        </div>
                    </>
                )}
            </IonContent>

            <IonFooter>
                <div style={{ display: "flex", gap: 8, padding: 12 }}>
                    <IonButton expand="block" fill="outline" onClick={onDismiss} disabled={sending}>
                        Cancel
                    </IonButton>
                    <IonButton
                        expand="block"
                        onClick={handleSend}
                        disabled={!canSend || sending}
                    >
                        {sending ? (
                            <>
                                <IonSpinner name="lines-small" style={{ marginRight: 8 }} />
                                Sending {progress.done}/{progress.total}
                                {progress.failed ? ` • ${progress.failed} failed` : ""}
                            </>
                        ) : (
                            "Send"
                        )}
                    </IonButton>
                </div>
            </IonFooter>
        </IonModal>
    );
}
